import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import ChessBoard from '../components/ChessBoard';
import MoveHistory from '../components/MoveHistory';
import SidePanel from '../components/SidePanel';
import GameOverModal from '../components/GameOverModal';
import LimitReachedModal from '../components/LimitReachedModal';
import { checkDailyLimit, incrementMatchCount, saveMatchResult } from '../services/gameService';

const Game = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [gameInProgress, setGameInProgress] = useState(false);
  const [matchesLeft, setMatchesLeft] = useState(1);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [gameResult, setGameResult] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [geminiCommentary, setGeminiCommentary] = useState('');
  const [boardKey, setBoardKey] = useState(0);
  const [isDark, setIsDark] = useState(false);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [autoPlayThinking, setAutoPlayThinking] = useState('');

  useEffect(() => {
    const checkDarkMode = () => {
      const savedTheme = localStorage.getItem('theme');
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(savedTheme === 'dark' || (!savedTheme && prefersDark));
    };
    
    checkDarkMode();
    
    // Listen for theme changes
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  const handleMoveUpdate = (moves, commentary) => {
    setMoveHistory(moves);
    setGeminiCommentary(commentary || '');
    console.log('handleMoveUpdate called with commentary:', commentary);
  };

  const handleMainMenu = () => {
    navigate('/game');
    window.location.reload();
  };

  useEffect(() => {
    checkLimit();
  }, []);

  const checkLimit = async () => {
    try {
      const limit = await checkDailyLimit();
      setMatchesLeft(limit.matchesLeft);
    } catch (error) {
      console.error('Error checking daily limit:', error);
    }
  };

  const handleNewGame = async () => {
    // Check if user can play
    if (matchesLeft <= 0) {
      setShowLimitModal(true);
      return;
    }

    // Start new game
    try {
      await incrementMatchCount();
      setMatchesLeft(prev => prev - 1);
      setGameInProgress(true);
      setMoveHistory([]);
      setGeminiCommentary('');
      setAutoPlayEnabled(false);
      setAutoPlayThinking('');
      setBoardKey(prev => prev + 1); // Force board re-render
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };

  const handleAutoPlayToggle = () => {
    setAutoPlayEnabled(prev => !prev);
    if (!autoPlayEnabled) {
      setAutoPlayThinking('Preparing to play...');
    } else {
      setAutoPlayThinking('');
    }
  };

  const handleAutoPlayMove = (thinking) => {
    setAutoPlayThinking(thinking || 'Calculating best move...');
  };

  // Store the draw accept function from ChessBoard
  const [acceptDrawFn, setAcceptDrawFn] = useState(null);

  const handleDrawOfferCallback = (acceptFn) => {
    setAcceptDrawFn(() => acceptFn);
  };

  const handleOfferDraw = () => {
    // AI decides randomly whether to accept (30% chance to accept)
    const aiAccepts = Math.random() < 0.3;
    
    if (aiAccepts) {
      // AI accepts the draw
      if (acceptDrawFn) {
        acceptDrawFn();
      }
    } else {
      // AI declines - show a toast or commentary
      setGeminiCommentary("Draw? No way! I'm playing to win!");
      alert(`${getSelectedModelInfo()?.displayName || 'AI'} declined your draw offer!`);
    }
  };

  const handleGameEnd = async (result, moves, finalFen, commentary) => {
    setGameResult(result);
    setMoveHistory(moves);
    setGeminiCommentary(commentary);
    setGameInProgress(false);
    setAutoPlayEnabled(false);
    setAutoPlayThinking('');

    // Save result to database
    try {
      await saveMatchResult(result, moves, finalFen);
      setShowGameOverModal(true);
    } catch (error) {
      console.error('Error saving game result:', error);
    }
  };

  const handleResign = async () => {
    if (window.confirm('Are you sure you want to resign?')) {
      await handleGameEnd('loss', moveHistory, '', 'You resigned. Better luck next time!');
    }
  };

  const models = [
    { id: 1, name: 'Gemini', displayName: 'MasterGemi', logo: '/gemini-logo.svg', available: true },
    { id: 2, name: 'GPT-oss', displayName: 'ChampGPT', logoLight: '/gpt-logo-light.svg', logoDark: '/gpt-logo-dark.svg', available: true },
    { id: 3, name: 'NVIDIA', displayName: 'NemoTron', logo: '/nvidia-logo.svg', available: true },
    { id: 4, name: 'Claude', displayName: 'ClaudeChess', available: false },
    { id: 5, name: 'LLaMA', displayName: 'LLaMAster', available: false },
    { id: 6, name: 'Mistral', displayName: 'MistralMind', available: false },
    { id: 7, name: 'DeepSeek', displayName: 'DeepSeekPro', available: false }
  ];

  const [selectedModel, setSelectedModel] = useState(1);
  
  const getSelectedModelInfo = () => {
    return models.find(m => m.id === selectedModel);
  };

  return (
    <div className="h-screen bg-background dark:bg-dark-background flex flex-col overflow-hidden">
      <Navbar />
      
      <div className="flex-1 flex main-content overflow-hidden">
        {/* Left Panel - Model Selection OR Move History */}
        {gameInProgress ? (
          <MoveHistory moves={moveHistory} aiName={getSelectedModelInfo()?.displayName || 'AI'} />
        ) : (
          <div className="w-80 bg-panel dark:bg-dark-panel border-r-2 border-border dark:border-dark-border p-6 overflow-y-auto">
            <h2 className="text-text-primary dark:text-dark-text-primary font-bold text-xl mb-6 border-b-2 border-border dark:border-dark-border pb-3">
              Choose Model
            </h2>
            <div className="space-y-3">
              {models.map((model) => (
                <button
                  key={model.id}
                  onClick={() => model.available && setSelectedModel(model.id)}
                  disabled={!model.available}
                  className={`w-full p-4 border-2 text-left transition-all ${
                    selectedModel === model.id && model.available
                      ? 'bg-text-primary dark:bg-dark-text-primary text-background dark:text-dark-background border-text-primary dark:border-dark-text-primary shadow-brutal'
                      : model.available
                      ? 'bg-panel dark:bg-dark-panel border-border dark:border-dark-border hover:shadow-brutal hover:translate-x-0.5 hover:translate-y-0.5'
                      : 'bg-panel/50 dark:bg-dark-panel/50 border-border/30 dark:border-dark-border/30 cursor-not-allowed opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {model.logo || model.logoLight || model.logoDark ? (
                      <img 
                        src={model.logo || (isDark ? model.logoDark : model.logoLight)} 
                        alt={model.name} 
                        className="w-8 h-8" 
                      />
                    ) : (
                      <div className="w-8 h-8 bg-border dark:bg-dark-border rounded-sm flex items-center justify-center text-xs font-mono text-text-secondary dark:text-dark-text-secondary">
                        AI
                      </div>
                    )}
                    <div className="flex-1">
                      <div className={`font-bold ${
                        selectedModel === model.id && model.available
                          ? 'text-background dark:text-dark-background'
                          : 'text-text-primary dark:text-dark-text-primary'
                      }`}>
                        {model.name}
                      </div>
                      {!model.available && (
                        <div className="text-xs text-text-secondary dark:text-dark-text-secondary mt-1 font-mono">
                          COMING SOON
                        </div>
                      )}
                    </div>
                    {selectedModel === model.id && model.available && (
                      <div className="text-background dark:text-dark-background text-xl">✓</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Center - Chess Board */}
        {gameInProgress ? (
          <ChessBoard 
            key={boardKey}
            onGameEnd={handleGameEnd}
            onMoveUpdate={handleMoveUpdate}
            username={userData?.username || 'Player'}
            selectedModel={selectedModel}
            aiName={getSelectedModelInfo()?.displayName || 'MasterGemi'}
            aiLogo={getSelectedModelInfo()?.logo || (isDark ? getSelectedModelInfo()?.logoDark : getSelectedModelInfo()?.logoLight)}
            autoPlayEnabled={autoPlayEnabled}
            onAutoPlayMove={handleAutoPlayMove}
            onDrawOffer={handleDrawOfferCallback}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-background dark:bg-dark-background dotted-bg">
            <div className="text-center animate-fadeInDown">
              <div className="mb-6 flex justify-center">
                <img 
                  src={getSelectedModelInfo()?.logo || (isDark ? getSelectedModelInfo()?.logoDark : getSelectedModelInfo()?.logoLight)} 
                  alt={getSelectedModelInfo()?.name} 
                  className="w-32 h-32 animate-pulse"
                />
              </div>
              <h2 className="text-text-primary dark:text-dark-text-primary text-3xl font-bold mb-4">
                Ready to Challenge {getSelectedModelInfo()?.displayName}?
              </h2>
              <p className="text-text-secondary dark:text-dark-text-secondary text-base mb-8">
                Click "New Game" to start playing
              </p>
              <button 
                onClick={handleNewGame}
                disabled={matchesLeft <= 0}
                className="btn btn-primary px-8 py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {matchesLeft > 0 ? 'Start New Game' : 'No Matches Left Today'}
              </button>
            </div>
          </div>
        )}
        
        {/* Right Panel - Side Panel */}
        <SidePanel 
          onNewGame={handleNewGame}
          onResign={handleResign}
          onOfferDraw={handleOfferDraw}
          matchesLeft={matchesLeft}
          geminiCommentary={geminiCommentary}
          gameInProgress={gameInProgress}
          aiName={getSelectedModelInfo()?.displayName || 'MasterGemi'}
          aiLogo={getSelectedModelInfo()?.logo || (isDark ? getSelectedModelInfo()?.logoDark : getSelectedModelInfo()?.logoLight)}
          autoPlayEnabled={autoPlayEnabled}
          onAutoPlayToggle={handleAutoPlayToggle}
          autoPlayThinking={autoPlayThinking}
          moveCount={moveHistory.length}
        />
      </div>

      {/* Modals */}
      {showGameOverModal && (
        <GameOverModal 
          result={gameResult}
          stats={userData?.stats || { wins: 0, losses: 0, draws: 0 }}
          modelName={getSelectedModelInfo()?.displayName || 'MasterGemi'}
          onPlayAgain={() => {
            setShowGameOverModal(false);
            handleNewGame();
          }}
          onMainMenu={handleMainMenu}
          onClose={() => setShowGameOverModal(false)}
        />
      )}

      {showLimitModal && (
        <LimitReachedModal 
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </div>
  );
};

export default Game;
